import * as React from 'react';
import { useTranslation } from 'react-i18next';

import type { Emoji } from '@emoji-mart/data';
import { type EmojiCategoryList, type EmojiIconList, EmojiSettings, type GridRow } from '@platejs/emoji';
import {
	type EmojiDropdownMenuOptions,
	useEmojiDropdownMenuState,
	type UseEmojiPickerType,
} from '@platejs/emoji/react';

import { Flex, Input, Popover, theme, Tooltip, Typography } from 'antd';
import {
	AppleIcon,
	ClockIcon,
	CompassIcon,
	FlagIcon,
	LeafIcon,
	LightbulbIcon,
	MusicIcon,
	SearchIcon,
	SmileIcon,
	StarIcon,
	VolleyballIcon,
	XIcon,
} from 'lucide-react';

import { EMOJI_ICON_SIZE, FIXED_ICON_SIZE, ToolbarButton } from 'features/Editor';

import styles from './EmojiToolbar.module.scss';

export function EmojiToolbarButton({
	options,
	...props
}: {
	options?: EmojiDropdownMenuOptions;
} & React.ComponentPropsWithoutRef<typeof ToolbarButton>) {
	const { emojiPickerState, isOpen, setIsOpen } = useEmojiDropdownMenuState(options);
	const { t } = useTranslation();

	return (
		<EmojiPopover
			control={
				<ToolbarButton
					pressed={isOpen}
					tooltip={isOpen ? undefined : t('Эмодзи')}
					icon={<SmileIcon size={FIXED_ICON_SIZE} />}
					{...props}
				/>
			}
			isOpen={isOpen}
			setIsOpen={setIsOpen}>
			<EmojiPicker {...emojiPickerState} isOpen={isOpen} setIsOpen={setIsOpen} settings={options?.settings} />
		</EmojiPopover>
	);
}

export function EmojiPopover({
	children,
	control,
	isOpen,
	setIsOpen,
}: {
	children: React.ReactNode;
	control: React.ReactNode;
	isOpen: boolean;
	setIsOpen: (open: boolean) => void;
}) {
	const {
		token: { colorBgLayout },
	} = theme.useToken();
	return (
		<Popover
			content={children}
			trigger="click"
			open={isOpen}
			onOpenChange={setIsOpen}
			styles={{ body: { padding: 0 } }}
			classNames={{ body: styles.popover }}
			color={colorBgLayout}
			destroyOnHidden>
			{control}
		</Popover>
	);
}

export function EmojiPicker({
	emoji,
	emojiLibrary,
	focusedCategory,
	hasFound,
	i18n,
	icons = {
		categories: emojiCategoryIcons,
		search: emojiSearchIcons,
	},
	isSearching,
	refs,
	searchResult,
	searchValue,
	setSearch,
	settings = EmojiSettings,
	visibleCategories,
	handleCategoryClick,
	onMouseOver,
	onSelectEmoji,
}: Omit<UseEmojiPickerType, 'icons'> & {
	icons?: EmojiIconList<React.ReactElement>;
}) {
	return (
		<Flex vertical className={styles.emojiToolbar}>
			<EmojiPickerNavigation
				onClick={handleCategoryClick}
				emojiLibrary={emojiLibrary}
				focusedCategory={focusedCategory}
				i18n={i18n}
				icons={icons}
			/>
			<EmojiPickerSearchBar i18n={i18n} searchValue={searchValue} setSearch={setSearch} />
			<EmojiPickerContent
				onMouseOver={onMouseOver}
				onSelectEmoji={onSelectEmoji}
				emojiLibrary={emojiLibrary}
				i18n={i18n}
				isSearching={isSearching}
				refs={refs}
				searchResult={searchResult}
				settings={settings}
				visibleCategories={visibleCategories}
			/>
			<EmojiPickerPreview emoji={emoji} hasFound={hasFound} isSearching={isSearching} />
		</Flex>
	);
}

const EmojiButton = React.memo(function EmojiButton({
	emoji,
	index,
	onMouseOver,
	onSelect,
}: {
	emoji: Emoji;
	index: number;
	onMouseOver: (emoji?: Emoji) => void;
	onSelect: (emoji: Emoji) => void;
}) {
	return (
		<button
			className={styles.emojiButton}
			onClick={() => onSelect(emoji)}
			onMouseEnter={() => onMouseOver(emoji)}
			onMouseLeave={() => onMouseOver()}
			aria-label={emoji.skins[0].native}
			data-index={index}
			tabIndex={-1}
			type="button">
			<span className="relative" data-emoji-set="native">
				{emoji.skins[0].native}
			</span>
		</button>
	);
});

const RowOfButtons = React.memo(function RowOfButtons({
	emojiLibrary,
	row,
	onMouseOver,
	onSelectEmoji,
}: {
	row: GridRow;
} & Pick<UseEmojiPickerType, 'emojiLibrary' | 'onMouseOver' | 'onSelectEmoji'>) {
	return (
		<Flex style={{ width: '100%' }} key={row.id} data-index={row.id}>
			{row.elements.map((emojiId, index) => (
				<EmojiButton
					key={emojiId}
					onMouseOver={onMouseOver}
					onSelect={onSelectEmoji}
					emoji={emojiLibrary.getEmoji(emojiId)}
					index={index}
				/>
			))}
		</Flex>
	);
});

function EmojiPickerContent({
	emojiLibrary,
	i18n,
	isSearching = false,
	refs,
	searchResult,
	visibleCategories,
	onMouseOver,
	onSelectEmoji,
}: Pick<
	UseEmojiPickerType,
	| 'emojiLibrary'
	| 'i18n'
	| 'isSearching'
	| 'onMouseOver'
	| 'onSelectEmoji'
	| 'refs'
	| 'searchResult'
	| 'settings'
	| 'visibleCategories'
>) {
	const { t } = useTranslation();

	const isCategoryVisible = React.useCallback(
		(categoryId: EmojiCategoryList) => (visibleCategories.has(categoryId) ? visibleCategories.get(categoryId) : false),
		[visibleCategories],
	);

	const EmojiList = React.useCallback(
		() =>
			emojiLibrary
				.getGrid()
				.sections()
				.map(({ id: categoryId }) => {
					const section = emojiLibrary.getGrid().section(categoryId);

					return (
						<div key={categoryId} ref={section.root} style={{ width: 256 }} data-id={categoryId}>
							<div className={styles.title}>{i18n.categories[categoryId]}</div>

							<Flex className={styles.content} style={{ height: section.getRows().length * 30 }}>
								{isCategoryVisible(categoryId) &&
									section
										.getRows()
										.map((row: GridRow) => (
											<RowOfButtons
												key={row.id}
												onMouseOver={onMouseOver}
												onSelectEmoji={onSelectEmoji}
												emojiLibrary={emojiLibrary}
												row={row}
											/>
										))}
							</Flex>
						</div>
					);
				}),
		[emojiLibrary, i18n.categories, isCategoryVisible, onSelectEmoji, onMouseOver],
	);

	const SearchList = React.useCallback(
		() => (
			<div style={{ width: 256 }} data-id="search">
				<div className={styles.title}>{t('Результаты поиска')}</div>
				<Flex className={styles.content}>
					{searchResult.map((emoji: Emoji, index: number) => (
						<EmojiButton
							key={emoji.id}
							onMouseOver={onMouseOver}
							onSelect={onSelectEmoji}
							emoji={emojiLibrary.getEmoji(emoji.id)}
							index={index}
						/>
					))}
				</Flex>
			</div>
		),
		[emojiLibrary, searchResult, onSelectEmoji, onMouseOver, t],
	);

	return (
		<div ref={refs.current.contentRoot} className={styles.contentWrapper} data-id="scroll">
			<div ref={refs.current.content} className={styles.contentContainer}>
				{isSearching ? SearchList() : EmojiList()}
			</div>
		</div>
	);
}

function EmojiPickerSearchBar({
	searchValue,
	setSearch,
}: Pick<UseEmojiPickerType, 'i18n' | 'searchValue' | 'setSearch'>) {
	const { t } = useTranslation();
	return (
		<div className={styles.search}>
			<Input
				autoFocus
				allowClear
				placeholder={t('Поиск эмодзи')}
				value={searchValue}
				onChange={(event) => setSearch(event.target.value)}
				prefix={<SearchIcon size={14} className={styles.searchIcon} />}
			/>
		</div>
	);
}

function EmojiPreview({ emoji }: Pick<UseEmojiPickerType, 'emoji'>) {
	return (
		<Flex gap={12} align="center" className={styles.preview}>
			<div>{emoji?.skins[0].native}</div>
			<Flex vertical>
				<Typography.Text strong style={{ fontSize: 12, maxWidth: '220px' }} ellipsis>
					{emoji?.name}
				</Typography.Text>
				<Typography.Text type="secondary" style={{ fontSize: 12, maxWidth: '220px' }} ellipsis>
					{`:${emoji?.id}:`}
				</Typography.Text>
			</Flex>
		</Flex>
	);
}

function NoEmoji() {
	const { t } = useTranslation();
	return (
		<Flex gap={12} align="center" className={styles.preview}>
			<div>😢</div>
			<Flex vertical>
				<Typography.Text strong style={{ fontSize: 12 }}>
					{t('Ничего не найдено')}
				</Typography.Text>
				<Typography.Text type="secondary" style={{ fontSize: 12 }}>
					{t('Данный эмодзи не удалось найти.')}
				</Typography.Text>
			</Flex>
		</Flex>
	);
}

function PickAnEmoji() {
	const { t } = useTranslation();
	return (
		<Flex gap={12} align="center" className={styles.preview}>
			<div>☝️</div>
			<Flex vertical>
				<Typography.Text strong style={{ fontSize: 12 }}>
					{t('Выберите эмодзи')}
				</Typography.Text>
				<Typography.Text type="secondary" style={{ fontSize: 12 }}>
					{t('...')}
				</Typography.Text>
			</Flex>
		</Flex>
	);
}

function EmojiPickerPreview({
	emoji,
	hasFound = true,
	isSearching = false,
	...props
}: Pick<UseEmojiPickerType, 'emoji' | 'hasFound' | 'isSearching'>) {
	const showPickEmoji = !emoji && (!isSearching || hasFound);
	const showNoEmoji = isSearching && !hasFound;
	const showPreview = emoji && !showNoEmoji && !showNoEmoji;

	return (
		<>
			{showPreview && <EmojiPreview emoji={emoji} {...props} />}
			{showPickEmoji && <PickAnEmoji {...props} />}
			{showNoEmoji && <NoEmoji {...props} />}
		</>
	);
}

function EmojiPickerNavigation({
	emojiLibrary,
	focusedCategory,
	i18n,
	icons,
	onClick,
}: {
	onClick: (id: EmojiCategoryList) => void;
} & Pick<UseEmojiPickerType, 'emojiLibrary' | 'focusedCategory' | 'i18n' | 'icons'>) {
	return (
		<Flex gap={2} justify="space-between" className={styles.menu}>
			{emojiLibrary
				.getGrid()
				.sections()
				.map(({ id }) => (
					<Tooltip key={id} title={i18n.categories[id]} styles={{ body: { lineHeight: '24px', fontSize: 12 } }}>
						<ToolbarButton
							pressed={id === focusedCategory}
							onClick={() => {
								onClick(id);
							}}
							aria-label={i18n.categories[id]}
							icon={icons.categories[id].outline}
						/>
					</Tooltip>
				))}
		</Flex>
	);
}

const emojiCategoryIcons: Record<
	EmojiCategoryList,
	{
		outline: React.ReactElement;
		solid: React.ReactElement; // Needed to add another solid variant - outline will be used for now
	}
> = {
	activity: {
		outline: <VolleyballIcon size={EMOJI_ICON_SIZE} />,
		solid: <VolleyballIcon size={EMOJI_ICON_SIZE} />,
	},

	custom: {
		outline: <StarIcon size={EMOJI_ICON_SIZE} />,
		solid: <StarIcon size={EMOJI_ICON_SIZE} />,
	},

	flags: {
		outline: <FlagIcon size={EMOJI_ICON_SIZE} />,
		solid: <FlagIcon size={EMOJI_ICON_SIZE} />,
	},

	foods: {
		outline: <AppleIcon size={EMOJI_ICON_SIZE} />,
		solid: <AppleIcon size={EMOJI_ICON_SIZE} />,
	},

	frequent: {
		outline: <ClockIcon size={EMOJI_ICON_SIZE} />,
		solid: <ClockIcon size={EMOJI_ICON_SIZE} />,
	},

	nature: {
		outline: <LeafIcon size={EMOJI_ICON_SIZE} />,
		solid: <LeafIcon size={EMOJI_ICON_SIZE} />,
	},

	objects: {
		outline: <LightbulbIcon size={EMOJI_ICON_SIZE} />,
		solid: <LightbulbIcon size={EMOJI_ICON_SIZE} />,
	},

	people: {
		outline: <SmileIcon size={EMOJI_ICON_SIZE} />,
		solid: <SmileIcon size={EMOJI_ICON_SIZE} />,
	},

	places: {
		outline: <CompassIcon size={EMOJI_ICON_SIZE} />,
		solid: <CompassIcon size={EMOJI_ICON_SIZE} />,
	},

	symbols: {
		outline: <MusicIcon size={EMOJI_ICON_SIZE} />,
		solid: <MusicIcon size={EMOJI_ICON_SIZE} />,
	},
};

const emojiSearchIcons = {
	delete: <XIcon size={EMOJI_ICON_SIZE} />,
	loupe: <SearchIcon size={EMOJI_ICON_SIZE} />,
};
